import React, { useEffect, useState } from 'react';
import { Box, TextField, Typography } from '@mui/material';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import DosenConfirmModal from '../../../components/DosenConfirmModal';
import DosenButton from '../../../../../components/ui/Buttons/DosenButton';
import type { LessonTagChoice } from '../lessonTags';

export interface LessonPurposeOption {
  choice: LessonTagChoice;
  label: string;
  /** Beside the label, e.g. how many Scored Roleplays a capability already has. */
  note?: string;
}

const keyOf = (c: LessonTagChoice) => ('capabilityId' in c ? `c:${c.capabilityId}` : `o:${c.outcome}`);

/**
 * Asks what a lesson is for before anything is generated for it
 * (docs/PRD-capability-lesson-roleplays.md R1, R9): a lesson added in the
 * Content step (with its title), an outcome lesson whose Roleplay is switched
 * on (only a capability can be picked then), and Change on a lesson's
 * capability. With no options it asks for the title alone.
 */
const LessonPurposeDialog: React.FC<{
  open: boolean;
  title: string;
  body?: string;
  options: LessonPurposeOption[];
  /** The option picked when it opens. */
  initial?: LessonTagChoice | null;
  /** Ask for the lesson's title too (a lesson being added). */
  titleField?: { label: string; initial: string };
  confirmLabel: string;
  onCancel: () => void;
  onConfirm: (choice: LessonTagChoice | null, title: string) => void;
}> = ({ open, title, body, options, initial, titleField, confirmLabel, onCancel, onConfirm }) => {
  const [picked, setPicked] = useState<string | null>(null);
  const [name, setName] = useState('');

  useEffect(() => {
    if (!open) return;
    setPicked(initial ? keyOf(initial) : options[0] ? keyOf(options[0].choice) : null);
    setName(titleField?.initial ?? '');
    // Only when it opens: the admin's pick stands while it is open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const chosen = options.find((o) => keyOf(o.choice) === picked) ?? null;
  const ready = (options.length === 0 || Boolean(chosen)) && (!titleField || name.trim().length > 0);

  return (
    <DosenConfirmModal open={open} onClose={onCancel} title={title}>
      {body && (
        <Typography
          sx={{ color: 'var(--Text-text-secondary, #B6B6B6)', fontSize: 14, fontFamily: 'Inter', lineHeight: '20px' }}
        >
          {body}
        </Typography>
      )}
      {titleField && (
        <TextField
          label={titleField.label}
          value={name}
          onChange={(e) => setName(e.target.value)}
          size="small"
          autoFocus
          fullWidth
          inputProps={{ maxLength: 200 }}
        />
      )}
      {options.length > 0 && (
        <Box role="radiogroup" aria-label={title} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {options.map((o) => {
            const key = keyOf(o.choice);
            const selected = key === picked;
            return (
              <Box
                key={key}
                role="radio"
                aria-checked={selected}
                tabIndex={0}
                onClick={() => setPicked(key)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') setPicked(key);
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  px: 1.5,
                  py: 1,
                  borderRadius: '6px',
                  cursor: 'pointer',
                  border: `1px solid ${
                    selected ? 'var(--Stroke-highlight, #F79420)' : 'var(--Stroke-Border-border-dim, #2A2A2A)'
                  }`
                }}
              >
                {selected ? (
                  <RadioButtonCheckedIcon sx={{ fontSize: 18, color: 'var(--Stroke-highlight, #F79420)' }} />
                ) : (
                  <RadioButtonUncheckedIcon sx={{ fontSize: 18, color: 'var(--Text-text-tertiary, #868686)' }} />
                )}
                <Typography
                  sx={{ flex: 1, fontSize: 14, fontFamily: 'Inter', color: 'var(--Text-text-primary, #FDFDFD)' }}
                >
                  {o.label}
                </Typography>
                {o.note && (
                  <Typography sx={{ fontSize: 12, fontFamily: 'Inter', color: 'var(--Text-text-tertiary, #868686)' }}>
                    {o.note}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
      )}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <DosenButton tone="basic" sizeVariant="sm" onClick={onCancel}>
          Cancel
        </DosenButton>
        <DosenButton
          sizeVariant="sm"
          disabled={!ready}
          onClick={() => ready && onConfirm(chosen?.choice ?? null, name.trim())}
        >
          {confirmLabel}
        </DosenButton>
      </Box>
    </DosenConfirmModal>
  );
};

export default LessonPurposeDialog;
